import { Injectable } from "@angular/core";
import { tap } from "rxjs";
// Store
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
	SetIntegration,
	SetIntegrationMenu,
	ClearSelectedIntegration,
	SetIntegrations,
	SetIntegrationOrganization,
	SetIntegrationBackUrl,
	SetIntegrationOrganizationMenu,
	ClearIntegrationOrganization,
	ClearIntegrationOrganizationMenu
} from "./actions/generic-integation.actions";
// Service
import { GenericIntegrationService } from "../../service/generic-integration.service";
// Model
import { GenericIntegrationStateModel, GenericIntegration_STATE_PATH } from "./model/generic-integration-state.model";
import { IntegrationOrganizationItemModel, IntegrationsModel } from "../models/integrations.model";
import { SelectedGenericIntegrationService } from "../services/selected-generic-integration.service";
import { ObjectUtils } from "@cv-invision/core";

@State<GenericIntegrationStateModel>({
	name: GenericIntegration_STATE_PATH,
	defaults: new GenericIntegrationStateModel()
})
@Injectable()
export class GenericIntegrationState {

	constructor(
		private genericIntegrationService: GenericIntegrationService,
		private selectedIntegrationService: SelectedGenericIntegrationService
	) {}

	/**
	 * Get integrations.
	 * @param state: GenericIntegrationStateModel
	 */
	@Selector()
	static getIntegrations(state: GenericIntegrationStateModel): IntegrationsModel[] {
		return state.integrations;
	}

	/**
	 * Get selected integration.
	 * @param state: GenericIntegrationStateModel
	 */
	@Selector()
	static getIntegration(state: GenericIntegrationStateModel): IntegrationsModel {
		return state.integration;
	}

	/**
	 * Get the left menu.
	 * @param state: GenericIntegrationStateModel
	 */
	@Selector()
	static getIntegrationMenu(state: GenericIntegrationStateModel): any[] {
		return state.menu;
	}

	/**
	 * Get slected organization
	 * @param state: GenericIntegrationStateModel
	 */
	@Selector()
	static getIntegrationOrganization(state: GenericIntegrationStateModel): IntegrationOrganizationItemModel {
		return state.organization;
	}

	/**
	 * Get the backUrl.
	 * @param state: GenericIntegrationStateModel
	 */
	@Selector()
	static getIntegrationBackUrl(state: GenericIntegrationStateModel): string {
		return state.backUrl;
	}

	/**
	 * set integrations list
	 * @param ctx
	 */
	@Action(SetIntegrations)
	setIntegrations(ctx: StateContext<GenericIntegrationStateModel>, { integrations }: SetIntegrations): void {
		ctx.patchState({
			integrations
		});
	}

	/**
	 * set integration
	 * @param ctx
	 */
	@Action(SetIntegration)
	setIntegration(ctx: StateContext<GenericIntegrationStateModel>, { integration }: SetIntegration): void {
		ctx.patchState({
			integration
		});
	}

	/**
	 * set integration
	 * @param ctx
	 */
	@Action(SetIntegrationMenu)
	setIntegrationMenu(ctx: StateContext<GenericIntegrationStateModel>, { integration }: SetIntegrationMenu): any {
		return this.genericIntegrationService.getIntegrationMappers(integration).pipe(
			tap((data: string[]) => {
				const baseUrl = `/global-configuration/generic-integration/${integration}`;
				const menu = this.selectedIntegrationService.getMenu(baseUrl, data);

				ctx.patchState({
					mappers: data,
					menu
				});
			})
		)
	}

	/**
	 * set integration organization selected
	 * @param ctx
	 */
	@Action(SetIntegrationOrganization)
	setIntegrationOrganization(ctx: StateContext<GenericIntegrationStateModel>, { organization }: SetIntegrationOrganization): void {
		ctx.patchState({
			organization
		});
	}

	/**
	 * clear integration organization selected
	 * @param ctx
	 */
	@Action(ClearIntegrationOrganization)
	clearIntegrationOrganization(ctx: StateContext<GenericIntegrationStateModel>): void {
		ctx.patchState({
			organization: null
		});
	}

	/**
	 * set integration organization selected MENU
	 * @param ctx
	 */
	@Action(SetIntegrationOrganizationMenu)
	setIntegrationOrganizationMenu(ctx: StateContext<GenericIntegrationStateModel>): any {
		const state = ctx.getState();
		const baseUrl = `/global-configuration/generic-integration/${state.integration.integrationId}/organization/${state.organization.integrationId}`;
		const mappers = state.mappers;
		const menu = ObjectUtils.recursiveDeepCopy(state.menu);
		const orgMenu = this.selectedIntegrationService.getMenu(baseUrl, mappers, false);

		menu.push({
			label: state.organization.classOrganizationName,
			customIconClass: 'fas fa-building',
			active: true,
			items: orgMenu
		});

		ctx.patchState({
			menu
		});
	}

	/**
	 * set integration organization selected MENU
	 * @param ctx
	 */
	@Action(ClearIntegrationOrganizationMenu)
	clearIntegrationOrganizationMenu(ctx: StateContext<GenericIntegrationStateModel>): any {
		const state = ctx.getState();
		const menu = ObjectUtils.recursiveDeepCopy(state.menu);

		if (menu[menu.length -1] && menu[menu.length -1].customIconClass === 'fas fa-building') {
			menu.pop();
		}

		ctx.patchState({
			menu
		});
	}

	/**
	 * set integration module backUrl
	 * @param ctx
	 */
	@Action(SetIntegrationBackUrl)
	setIntegrationBackUrl(ctx: StateContext<GenericIntegrationStateModel>, { url }: SetIntegrationBackUrl): void {
		ctx.patchState({
			backUrl: url
		});
	}

	/**
	 * clear selected integration
	 * @param ctx
	 */
	@Action(ClearSelectedIntegration)
	clearSelectedIntegration(ctx: StateContext<GenericIntegrationStateModel>): void {
		ctx.setState(new GenericIntegrationStateModel());
	}
}
